import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { supabase } from '../../lib/supabaseClient';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Controller } from 'react-hook-form';
import { AlertCircle, UserPlus, Users, Edit, Trash2 } from 'lucide-react';
import EditEmployeeModal from './components/EditEmployeeModal';
import DeleteEmployeeConfirmationModal from './components/DeleteEmployeeConfirmationModal';
import { useSelector } from 'react-redux';
import { useData } from '../../context/DataContext';
import { useQueryClient } from '@tanstack/react-query';
import BulkImportEmployees from './components/BulkImportEmployees';

const EmployeeManagement = () => {
  const { register, handleSubmit, reset, control, formState: { errors, isSubmitting } } = useForm();
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [departments, setDepartments] = useState([]);
  const [showEditEmployeeModal, setShowEditEmployeeModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);

  const queryClient = useQueryClient();
  const { personnel: employees, isLoading: loading, currentUser } = useData();
  const isPic = currentUser?.role === 'pic' || currentUser?.role === 'department_pic';
  const canDelete = currentUser?.role === 'admin' || currentUser?.role === 'system_admin';

  const fetchDepartments = useCallback(async () => {
    const { data: deptData, error: deptError } = await supabase.from('departments').select('id, name');
    if (deptError) {
      setError('Failed to fetch departments.');
    } else {
      setDepartments(deptData.map(d => ({ value: d.id, label: d.name })));
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const onSubmit = async (data) => {
    setSuccess('');
    setError('');
    try {
      const { error } = await supabase.from('employees').insert({
        full_name: data.fullName,
        email: data.email,
        employee_number: data.employeeNumber,
        department_id: parseInt(data.department_id),
      });
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['allPersonnel'] });
      setSuccess(`Employee ${data.fullName} added successfully.`);
      reset();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Users className="text-primary" /> Employee Management
        </h1>
        {!isPic && (
          <Button
            variant="outline"
            onClick={() => setShowBulkImportModal(true)}
            className="flex items-center gap-2"
          >
            <UserPlus size={16} /> Bulk Import
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column: Add Employee Form */}
        {!isPic && (
          <div className="lg:col-span-1">
            <div className="bg-card p-8 rounded-xl shadow-lg border border-border">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-foreground">
                <UserPlus size={24} /> Add New Employee
              </h2>
              {success && <div className="bg-green-100 text-green-700 p-3 rounded-lg mb-4">{success}</div>}
              {error && <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4 flex items-center gap-2"><AlertCircle size={20} />{error}</div>}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <Input label="Full Name" type="text" className="bg-card" {...register('fullName', { required: 'Full Name is required' })} error={errors.fullName?.message} />
                <Input label="Email" type="email" className="bg-card" {...register('email')} error={errors.email?.message} />
                <Input label="Employee ID" type="text" className="bg-card" {...register('employeeNumber')} error={errors.employeeNumber?.message} />
                <Controller
                  name="department_id"
                  control={control}
                  defaultValue=""
                  rules={{ required: 'Department is required' }}
                  render={({ field }) => (
                    <Select 
                      {...field} 
                      label="Department" 
                      options={departments} 
                      error={errors.department_id?.message}
                    />
                  )}
                />
                <Button type="submit" loading={isSubmitting} disabled={isSubmitting} className="w-full">
                  {isSubmitting ? 'Adding...' : 'Add Employee'}
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* Right Column: Employee List */}
        <div className={isPic ? "lg:col-span-3" : "lg:col-span-2"}>
          <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
            <div className="px-6 py-5 border-b border-border flex items-center gap-3">
              <Users size={20} className="text-primary" />
              <h2 className="text-lg font-bold text-foreground">{isPic ? 'My Team' : 'Employee List'}</h2>
            </div>
            <div className="overflow-auto max-h-[calc(100vh-250px)] relative">
              <table className="min-w-full divide-y divide-border/60 dark:divide-border/35">
                <thead className="bg-muted sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Name</th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Employee ID</th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Department</th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Email</th>
                    <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-muted-foreground uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border/50 dark:divide-border/30">
                  {loading ? (
                    <tr><td colSpan="5" className="text-center py-10 text-muted-foreground/70">Loading...</td></tr>
                  ) : employees.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-10 text-muted-foreground/70">No employees found.</td></tr>
                  ) : (
                    employees.map(emp => (
                      <tr key={emp.id} className="hover:bg-muted/80 transition-colors group">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-foreground">{emp.full_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{emp.employee_number || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted/50 text-foreground/90">
                            {emp.departments?.name || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{emp.email || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {!isPic && (
                            <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-primary hover:bg-blue-50"
                                onClick={() => {
                                  setEditingEmployee(emp);
                                  setShowEditEmployeeModal(true);
                                }}
                              >
                                Edit
                              </Button>
                              {canDelete && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    setEmployeeToDelete(emp);
                                    setShowDeleteConfirmModal(true);
                                  }}
                                >
                                  Delete
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {showEditEmployeeModal && editingEmployee && (
          <EditEmployeeModal
            employee={editingEmployee}
            departments={departments}
            onClose={() => {
              setShowEditEmployeeModal(false);
              setEditingEmployee(null);
            }}
            onEmployeeUpdated={() => {
              queryClient.invalidateQueries({ queryKey: ['allPersonnel'] });
              setSuccess('Employee updated successfully!');
            }}
          />
        )}

        {showDeleteConfirmModal && employeeToDelete && (
          <DeleteEmployeeConfirmationModal
            employee={employeeToDelete}
            onClose={() => {
              setShowDeleteConfirmModal(false);
              setEmployeeToDelete(null);
            }}
            onConfirmDelete={() => {
              queryClient.invalidateQueries({ queryKey: ['allPersonnel'] });
              setSuccess('Employee deleted successfully!');
            }}
          />
        )}

        {showBulkImportModal && (
          <BulkImportEmployees
            isOpen={showBulkImportModal}
            onClose={() => setShowBulkImportModal(false)}
            onImportComplete={(msg) => {
              queryClient.invalidateQueries({ queryKey: ['allPersonnel'] });
              setSuccess(msg);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default EmployeeManagement;
